import { useEffect, useRef, useState } from 'react';
import { ApiUserRole, type ExecutorResponse, type OperatorTaskRelatedResponse } from '@/core/generated/api';
import { ClientSearchField } from '@/entities/Client/ui/ClientSearchField';
import { ExecutorSearchField } from '@/entities/Executor/ui/ExecutorSearchField';
import { operatorTaskService } from '@/entities/OperatorTask/api/operatorTaskService';
import { TaskRelatedLinks } from '@/entities/OperatorTask/ui/TaskRelatedLinks';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { DropdownField } from '@/shared/ui/DropdownField/DropdownField';
import { EntityFormFields, EntityFormStack } from '@/shared/ui/EntityFormLayout/EntityFormLayout';
import { RequestEmpty, RequestError, RequestLoading } from '@/shared/ui/RequestState';
import { RequestStatus } from '@/shared/model/requestStatus';
import { getErrorMessage } from '@/shared/lib/errors';

const roles = [
    {
        id: ApiUserRole.Client,
        name: 'Клиент',
    },
    {
        id: ApiUserRole.Executor,
        name: 'Исполнитель',
    },
];

interface TaskChatSearchFieldProps {
    name: string;
    value: OperatorTaskRelatedResponse | null;
    onChange: (value: OperatorTaskRelatedResponse | null) => void;
    onBlur: () => void;
    disabled: boolean;
    error?: string;
    isRequired?: boolean;
    compact?: boolean;
}

/** Выбирает чат поддержки через поиск клиента или исполнителя */
export const TaskChatSearchField = (
    { name, value, onChange, onBlur, disabled, error, isRequired = true, compact = false }: TaskChatSearchFieldProps
) => {
    const [role, setRole] = useState(value?.companionRole?.id === ApiUserRole.Executor
        ? ApiUserRole.Executor
        : ApiUserRole.Client);

    const [clientId, setClientId] = useState<string | null>(null);
    const [executor, setExecutor] = useState<ExecutorResponse | null>(null);
    const [choices, setChoices] = useState<OperatorTaskRelatedResponse[]>([]);
    const [status, setStatus] = useState(RequestStatus.Idle);
    const [failure, setFailure] = useState<string | null>(null);
    const requestId = useRef(0);
    const selectedAppUserId = useRef<string | null>(null);

    const Layout = compact
        ? EntityFormFields
        : EntityFormStack;

    useEffect(() => () => { requestId.current += 1; }, []);

    /** Находит чат поддержки выбранного собеседника */
    const findChats = async (appUserId: string | null | undefined) => {
        const current = ++requestId.current;
        selectedAppUserId.current = appUserId ?? null;
        onChange(null);
        onBlur();
        setChoices([]);
        setFailure(null);

        if (!appUserId) {
            setStatus(RequestStatus.Idle);

            return;
        }

        setStatus(RequestStatus.Loading);

        try {
            const response = await operatorTaskService.chats(appUserId);

            if (current !== requestId.current) return;

            const available = (response.items ?? []).filter((chat) => chat.availability?.id === 'available'
                && chat.companionAppUserId === appUserId && chat.companionRole?.id === role)
                .slice(0, 7);

            setChoices(available);
            setStatus(RequestStatus.Success);

            if (available.length === 1) onChange(available[0]);
        } catch (reason: unknown) {
            if (current !== requestId.current) return;

            setFailure(getErrorMessage(reason, 'Не удалось найти чат поддержки'));
            setStatus(RequestStatus.Error);
        }
    };

    /** Очищает поиск при выборе другого типа собеседника */
    const changeRole = (next: typeof ApiUserRole.Client | typeof ApiUserRole.Executor) => {
        requestId.current += 1;
        selectedAppUserId.current = null;
        setRole(next);
        setClientId(null);
        setExecutor(null);
        setChoices([]);
        setStatus(RequestStatus.Idle);
        setFailure(null);
        onChange(null);
    };

    return (
        <Layout>
            <DropdownField
                name={`${name}-role`}
                label="Собеседник"
                value={role}
                options={roles}
                showClear={false}
                disabled={disabled}
                onChange={(event) => {
                    if (event.value === ApiUserRole.Client || event.value === ApiUserRole.Executor) changeRole(event.value);
                }}
            />
            {value
                ? (
                    <>
                        <TaskRelatedLinks context={{ chat: value }} />
                        <ActionButton
                            label="Выбрать другого собеседника"
                            type="button"
                            outlined
                            disabled={disabled}
                            onClick={() => changeRole(role)}
                        />
                    </>
                )
                : role === ApiUserRole.Client
                    ? (
                        <ClientSearchField
                            name={`${name}-client`}
                            label="Клиент"
                            value={clientId}
                            onChange={(id, client) => {
                                setClientId(id);
                                void findChats(client?.appUserId);
                            }}
                            onBlur={onBlur}
                            disabled={disabled}
                            isRequired={isRequired}
                            minSearchLength={0}
                            takeCount={7}
                            scrollHeight="18rem"
                            error={error}
                        />
                    )
                    : (
                        <ExecutorSearchField
                            name={`${name}-executor`}
                            label="Исполнитель"
                            value={executor}
                            onChange={(person) => {
                                setExecutor(person);
                                void findChats(person?.appUserId);
                            }}
                            disabled={disabled}
                            isRequired={isRequired}
                            minSearchLength={0}
                            takeCount={7}
                            scrollHeight="18rem"
                            error={error}
                        />
                    )}
            {status === RequestStatus.Loading &&
                <RequestLoading message="Поиск чата поддержки..." />
            }
            {status === RequestStatus.Error && (
                <RequestError
                    message={failure ?? 'Не удалось найти чат поддержки'}
                    onRetry={() => void findChats(selectedAppUserId.current)}
                    compact
                />
            )}
            {status === RequestStatus.Success && !choices.length && (
                <RequestEmpty message="У выбранного собеседника нет доступного чата с поддержкой" />
            )}
            {!value && choices.length > 1 && (
                <DropdownField
                    name={`${name}-chat`}
                    label="Чат поддержки"
                    value={null}
                    options={choices.map((chat) => ({
                        id: chat.chatId,
                        name: chat.label
                    }))}
                    disabled={disabled}
                    isRequired={isRequired}
                    onChange={(event) => onChange(choices.find((chat) => chat.chatId === event.value) ?? null)}
                />
            )}
        </Layout>
    );
};
